import {
  Badge, Button, Card, DataTable, DateInput, EmptyState, Field, Input, LoadingState, Notice, Permissions, Row, Stack,
  Textarea, describeError, invalidDateIn, useApi, useI18n, usePermission,
  type AcademicYearResponse, type AcademicYearStatus, type BadgeTone, type CalendarSpanRequest, type Column,
  type TermResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

const STATUS: Record<AcademicYearStatus, { label: string; tone: BadgeTone }> = {
  Current: { label: 'years.status.current', tone: 'success' },
  Planned: { label: 'years.status.planned', tone: 'neutral' },
  Closed: { label: 'years.status.closed', tone: 'info' },
};

/** The largest page the API serves, so that every year a nursery has kept is on one screen. */
const YEARS_PAGE_SIZE = 100;

/**
 * The academic calendar (Q33–Q35): years, their terms, and which year is current. The current year's first day
 * is where a member of staff's view of a child's history begins. A term that has ended is closed here, which
 * makes its records read only, and only the owner reopens one, with a reason (Q34).
 */
export function AcademicYears() {
  const api = useApi();
  const { t } = useI18n();
  const canManage = usePermission(Permissions.academic.manage);
  const canReopen = usePermission(Permissions.academic.reopen);
  const [creating, setCreating] = useState(false);

  const years = useQuery({
    queryKey: ['academic-years'],
    queryFn: () => api.academicYears.list({ pageSize: YEARS_PAGE_SIZE }),
  });

  return (
    <>
      <div className="dg-page-header">
        <div className="dg-page-header__row">
          <h1>{t('years.title')}</h1>
          {canManage && (
            <Button onClick={() => setCreating((current) => !current)} icon="＋">{t('years.new')}</Button>
          )}
        </div>
        <p className="dg-page-header__lede">{t('years.lede')}</p>
      </div>

      {creating && (
        <SpanForm
          title={t('years.new')}
          initial={{ name: '', startDate: '', endDate: '' }}
          nameLabel={t('years.field.name')}
          submitLabel={t('years.new.submit')}
          save={(body) => api.academicYears.create(body)}
          onDone={() => setCreating(false)}
        />
      )}

      {years.isPending && <LoadingState />}
      {years.isError && <ErrorNotice error={years.error} />}
      {years.data?.items.length === 0 && <EmptyState icon="📅" title={t('years.empty')} />}

      <Stack>
        {years.data?.items.map((year) => <YearCard key={year.id} year={year} canManage={canManage} canReopen={canReopen} />)}
      </Stack>
    </>
  );
}

interface YearCardProps {
  year: AcademicYearResponse;
  canManage: boolean;
  canReopen: boolean;
}

function YearCard({ year, canManage, canReopen }: YearCardProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, tn, formatDate, today } = useI18n();
  const [editing, setEditing] = useState(false);
  const [addingTerm, setAddingTerm] = useState(false);
  const [editingTerm, setEditingTerm] = useState<TermResponse | null>(null);
  const [reopening, setReopening] = useState<TermResponse | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['academic-years'] });
  const makeCurrent = useMutation({ mutationFn: () => api.academicYears.makeCurrent(year.id), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.academicYears.remove(year.id), onSuccess: refresh });
  const removeTerm = useMutation({ mutationFn: (id: number) => api.academicYears.removeTerm(id), onSuccess: refresh });
  const closeTerm = useMutation({ mutationFn: (id: number) => api.academicYears.closeTerm(id), onSuccess: refresh });

  const open = year.status !== 'Closed';
  const status = STATUS[year.status];

  const columns: Column<TermResponse>[] = [
    { key: 'name', header: t('years.term.name'), render: (term) => term.name },
    { key: 'start', header: t('years.field.start'), render: (term) => formatDate(term.startDate) },
    { key: 'end', header: t('years.field.end'), render: (term) => formatDate(term.endDate) },
    {
      key: 'status',
      header: t('years.term.status'),
      render: (term) => (
        <Stack>
          {term.status === 'Closed'
            ? <Badge tone="info">{t('years.term.closed')}</Badge>
            : <Badge tone="neutral">{t('years.term.open')}</Badge>}
          {term.reopenedAt && term.reopenReason && (
            <small>{t('years.term.reopenedNote', { date: formatDate(term.reopenedAt), reason: term.reopenReason })}</small>
          )}
        </Stack>
      ),
    },
  ];

  // A term is closed from the day after its last; before that, today's records are still to be written.
  const ended = (term: TermResponse) => term.endDate < today();

  if ((canManage && open) || canReopen) {
    columns.push({
      key: 'actions',
      header: t('years.actions'),
      render: (term) => (
        <Row>
          {canManage && open && (
            <Button variant="ghost" onClick={() => setEditingTerm(term)}>{t('action.edit')}</Button>
          )}
          {canManage && open && term.status === 'Open' && ended(term) && (
            <Button variant="ghost" busy={closeTerm.isPending} onClick={() => closeTerm.mutate(term.id)}>
              {t('years.term.close')}
            </Button>
          )}
          {canManage && open && term.status === 'Open' && (
            <Button variant="ghost" busy={removeTerm.isPending} onClick={() => removeTerm.mutate(term.id)}>
              {t('years.delete')}
            </Button>
          )}
          {canReopen && term.status === 'Closed' && (
            <Button variant="ghost" onClick={() => setReopening(term)}>{t('years.term.reopen')}</Button>
          )}
        </Row>
      ),
    });
  }

  return (
    <Card title={year.name} action={<Badge tone={status.tone}>{t(status.label)}</Badge>}>
      <Stack>
        <p>
          {t('years.span', { start: formatDate(year.startDate), end: formatDate(year.endDate) })}
          {' · '}
          {tn('years.classCount', year.classCount)}
        </p>

        {year.status === 'Current' && <Notice tone="info">{t('years.current.hint')}</Notice>}

        {[makeCurrent, remove, removeTerm, closeTerm].map((mutation, index) =>
          mutation.isError ? <ErrorNotice key={index} error={mutation.error} /> : null)}

        {canManage && open && (
          <Row>
            {year.status === 'Planned' && (
              <Button busy={makeCurrent.isPending} onClick={() => makeCurrent.mutate()}>
                {t('years.makeCurrent')}
              </Button>
            )}
            {year.status === 'Current' && (
              <Link className="dg-button" to={`/academic-years/${year.id}/rollover`}>{t('years.rollover')}</Link>
            )}
            <Button variant="ghost" onClick={() => setEditing((current) => !current)}>{t('action.edit')}</Button>
            <Button variant="ghost" onClick={() => setAddingTerm((current) => !current)} icon="＋">
              {t('years.term.new')}
            </Button>
            {year.status === 'Planned' && year.classCount === 0 && (
              <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate()}>
                {t('years.delete')}
              </Button>
            )}
          </Row>
        )}

        {editing && (
          <SpanForm
            title={t('years.edit')}
            initial={{ name: year.name, startDate: year.startDate, endDate: year.endDate }}
            nameLabel={t('years.field.name')}
            submitLabel={t('action.save')}
            save={(body) => api.academicYears.update(year.id, body)}
            onDone={() => setEditing(false)}
          />
        )}

        {addingTerm && (
          <SpanForm
            title={t('years.term.new')}
            initial={{ name: '', startDate: year.startDate, endDate: year.endDate }}
            nameLabel={t('years.term.name')}
            submitLabel={t('years.term.new.submit')}
            save={(body) => api.academicYears.addTerm(year.id, body)}
            onDone={() => setAddingTerm(false)}
          />
        )}

        {editingTerm && (
          <SpanForm
            key={editingTerm.id}
            title={t('years.term.edit')}
            initial={{ name: editingTerm.name, startDate: editingTerm.startDate, endDate: editingTerm.endDate }}
            nameLabel={t('years.term.name')}
            submitLabel={t('action.save')}
            save={(body) => api.academicYears.updateTerm(editingTerm.id, body)}
            onDone={() => setEditingTerm(null)}
          />
        )}

        {reopening && (
          <ReopenForm key={reopening.id} term={reopening} onDone={() => setReopening(null)} />
        )}

        <DataTable
          caption={t('years.terms.caption', { year: year.name })}
          columns={columns}
          rows={year.terms}
          rowKey={(term) => term.id}
          empty={<EmptyState icon="📅" title={t('years.terms.empty')} />}
        />
      </Stack>
    </Card>
  );
}

interface SpanFormProps {
  title: string;
  initial: CalendarSpanRequest;
  nameLabel: string;
  submitLabel: string;
  save: (body: CalendarSpanRequest) => Promise<unknown>;
  onDone: () => void;
}

/** A name and two dates: what a year and a term each consist of. */
function SpanForm({ title, initial, nameLabel, submitLabel, save, onDone }: SpanFormProps) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [form, setForm] = useState(initial);

  const mutation = useMutation({
    mutationFn: save,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['academic-years'] });
      void queryClient.invalidateQueries({ queryKey: ['classes'] });
      onDone();
    },
  });

  const set = (key: keyof CalendarSpanRequest, value: string) => setForm((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const unreadable = invalidDateIn(event.currentTarget);
    if (unreadable) { unreadable.focus(); return; }
    mutation.mutate({ ...form, name: form.name.trim() });
  }

  return (
    <Card title={title}>
      <form onSubmit={submit} noValidate>
        <Stack>
          {mutation.isError && <ErrorNotice error={mutation.error} />}

          <Field label={nameLabel} required>
            {(props) => <Input {...props} value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />}
          </Field>

          <Row>
            <Field label={t('years.field.start')} required>
              {(props) => (
                <DateInput {...props} value={form.startDate} onChange={(value) => set('startDate', value)} />
              )}
            </Field>
            <Field label={t('years.field.end')} required>
              {(props) => (
                <DateInput {...props} value={form.endDate} onChange={(value) => set('endDate', value)} />
              )}
            </Field>
          </Row>

          <Row>
            <Button type="submit" busy={mutation.isPending} busyLabel={t('action.saving')}>{submitLabel}</Button>
            <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/** Reopening a closed term: the owner's, and never without saying why (Q34). */
function ReopenForm({ term, onDone }: { term: TermResponse; onDone: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.academicYears.reopenTerm(term.id, reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['academic-years'] });
      onDone();
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <Card title={t('years.term.reopen.title', { name: term.name })}>
      <form onSubmit={submit} noValidate>
        <Stack>
          <Notice tone="warning">{t('years.term.reopen.hint')}</Notice>
          {mutation.isError && <ErrorNotice error={mutation.error} />}

          <Field label={t('years.term.reopen.reason')} required>
            {(props) => (
              <Textarea {...props} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            )}
          </Field>

          <Row>
            <Button type="submit" busy={mutation.isPending} busyLabel={t('action.saving')} disabled={!reason.trim()}>
              {t('years.term.reopen')}
            </Button>
            <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/** The API's own title and detail, which say what to do next (NFR-USE-05). */
function ErrorNotice({ error }: { error: unknown }) {
  const { t } = useI18n();
  const described = describeError(error, t);
  return (
    <Notice tone={described.tone} title={described.title} reference={described.reference ?? undefined}>
      {described.body}
    </Notice>
  );
}
